import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import imageCompression from 'browser-image-compression';

interface UploadedMedia {
  id: string;
  gcsPath: string;
  publicUrl: string;
  filename: string;
  size: number;
}

export const useSymptomMedia = () => {
  const [uploaded, setUploaded] = useState<UploadedMedia[]>([]);
  const [loading, setLoading] = useState(false);

  // Memoize cleanup function to prevent infinite re-renders
  const cleanup = useCallback(() => {
    setUploaded([]);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setUploaded([]);
    };
  }, []);

  const upload = async (files: File[], userId: string, carId: string): Promise<UploadedMedia[]> => {
    setLoading(true);
    
    try {
      const uploadPromises = files.map(async (file) => {
        // Compress image before upload to save bandwidth and storage
        console.log(`📸 Original file size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
        
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 0.5,        // Max 500KB
          maxWidthOrHeight: 1024, // Max dimension
          useWebWorker: true,     // Better performance
          fileType: 'image/jpeg'  // Convert to JPEG for better compression
        });
        
        console.log(`📸 Compressed file size: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
        console.log(`📸 Compression ratio: ${((1 - compressedFile.size / file.size) * 100).toFixed(1)}%`);
        
        // Generate a draft ID for temporary upload
        const draftId = `draft-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        // Get signed URL for upload
        const { data: uploadData, error: uploadError } = await supabase.functions.invoke('upload-car-image', {
          body: {
            fileName: compressedFile.name,
            userId,
            kind: 'symptom',
            symptomId: draftId, // Use draft ID for now
            contentType: compressedFile.type
          }
        });

        if (uploadError) {
          throw new Error(`Upload failed for ${file.name}: ${uploadError.message}`);
        }

        // Upload compressed file to GCS
        const uploadResponse = await fetch(uploadData.signedUrl, {
          method: 'PUT',
          body: compressedFile,
          headers: {
            'Content-Type': compressedFile.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${file.name} to GCS`);
        }

        return {
          id: draftId,
          gcsPath: uploadData.path,
          publicUrl: uploadData.publicUrl,
          filename: compressedFile.name,
          size: compressedFile.size
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setUploaded(uploadedFiles);
      
      // Add logging as suggested by consultant
      console.log('draftMedia payload →', uploadedFiles);
      
      return uploadedFiles;
    } catch (error) {
      console.error('Media upload failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    uploaded,
    upload,
    loading,
    cleanup
  };
}; 